import { useMemo, useRef, useState } from "react";
import { Combobox } from "@headlessui/react";
import { ChevronIcon, XIcon } from "@/assets/icons";
import FormError from "./FormError";
import { useCombinedNomenclatures } from "@/hooks/useClassifierQueries";
import { UiCombinedNomenclature } from "@/generated/ui-api";
import { useTranslation } from "react-i18next";
import { useDebounce } from "@/shared/hooks";
import {
  formatCode,
  getAllDescendants,
  getDirectChildren,
  getPathToCode,
} from "@/shared/utils/combinedNomenclatureUtils";

interface CombinedNomenclatureInputProps {
  label: string;
  value: string | null; // The selected code
  onChange: (code: string | null) => void;
  onBlur?: () => void;
  className?: string;
  disabled?: boolean;
  readOnly?: boolean;
  error?: string;
  errorParams?: Record<string, string | number>;
  required?: boolean;
  validationDate?: string;
}

export default function CombinedNomenclatureInput({
  label,
  value,
  onChange,
  onBlur,
  className = "",
  disabled = false,
  readOnly = false,
  error,
  errorParams,
  required = false,
  validationDate,
}: CombinedNomenclatureInputProps) {
  const { t } = useTranslation();

  const { data: allItems = [] } = useCombinedNomenclatures(validationDate);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 200);
  const inputRef = useRef<HTMLInputElement>(null);

  const path = useMemo(() => getPathToCode(value, allItems), [value, allItems]);
  const currentParent = path.length > 0 ? path[path.length - 1] : null;
  const currentParentCode = currentParent ? currentParent.code : null;

  const hasChildrenToDrillDown = useMemo(() => {
    return getDirectChildren(currentParentCode, allItems).length > 0;
  }, [currentParentCode, allItems]);

  const filteredItems = useMemo(() => {
    if (!debouncedQuery) {
      return getDirectChildren(currentParentCode, allItems);
    } else {
      const candidates = getAllDescendants(currentParentCode, allItems);
      const q = debouncedQuery.toLowerCase();

      return candidates
        .filter(i => i.code.toLowerCase().startsWith(q))
        .slice(0, 100);
    }
  }, [currentParentCode, allItems, debouncedQuery]);

  const handleSelect = (item: UiCombinedNomenclature | null) => {
    if (item) {
      onChange(item.code);
      setQuery("");
    }
  };

  const handleRemoveLevel = (index: number) => {
    if (disabled || readOnly) return;
    if (index === 0) {
      onChange(null);
    } else {
      onChange(path[index - 1].code);
    }
    setQuery("");
    inputRef.current?.focus();
  };

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-black mb-2">
        {label}
        {required && <span className="text-black ml-1">*</span>}
      </label>

      <div className="flex flex-col gap-2">
        {(!readOnly || path.length > 0) && (
          <div
            className={`border rounded-md bg-gray-50 border-gray-200 ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {path.length > 0 && (
              <div
                className={`flex flex-col bg-white rounded-t-md ${
                  !readOnly && !disabled && (hasChildrenToDrillDown || query)
                    ? "border-b border-gray-200"
                    : ""
                }`}
              >
                {path.map((item, idx) => (
                  <div
                    key={item.code}
                    style={{ paddingLeft: `${idx * 1.5}rem` }}
                    className={`relative text-left py-2 text-sm flex items-center justify-between gap-2 border-l-4 transition-colors px-3
                     ${
                       idx === path.length - 1
                         ? !hasChildrenToDrillDown
                           ? "border-green-500 bg-green-100 font-medium text-green-800"
                           : "border-primary font-medium text-black"
                         : "border-transparent text-gray-600"
                     }`}
                  >
                    <div className="flex items-center gap-2 overflow-hidden">
                      <span className="font-mono text-gray-500">
                        {formatCode(item.code)}
                      </span>
                      <span className="truncate">{item.title}</span>
                    </div>
                    {!readOnly && !disabled && (
                      <button
                        type="button"
                        onClick={() => handleRemoveLevel(idx)}
                        className="text-gray-400 hover:text-red-500 transition-colors p-1"
                      >
                        <XIcon size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {!readOnly && !disabled && (hasChildrenToDrillDown || query) && (
              <div
                className="p-1"
                style={{ paddingLeft: `${path.length * 1.5}rem` }}
              >
                <Combobox
                  as="div"
                  className="relative"
                  value={null}
                  onChange={handleSelect}
                  disabled={disabled}
                >
                  <Combobox.Button as="div" className="relative w-full">
                    <Combobox.Input
                      ref={inputRef}
                      className="w-full px-3 py-2 text-sm bg-transparent border-none focus:ring-0 placeholder-gray-400"
                      onChange={event => setQuery(event.target.value)}
                      onBlur={() => {
                        if (!filteredItems.some(item => item.code === query)) {
                          setQuery("");
                        }
                      }}
                      displayValue={() => query}
                      placeholder={
                        path.length === 0
                          ? t("combinedNomenclatureInput.searchPlaceholder")
                          : t(
                              "combinedNomenclatureInput.searchDeeperPlaceholder"
                            )
                      }
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <ChevronIcon
                        size={16}
                        className="text-gray-400"
                        direction="down"
                      />
                    </div>
                  </Combobox.Button>

                  <Combobox.Options className="absolute left-0 w-full z-20 mt-1 bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    {filteredItems.length === 0 ? (
                      <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                        {query
                          ? t("combinedNomenclatureInput.nothingFound")
                          : t("combinedNomenclatureInput.noMoreItems")}
                      </div>
                    ) : (
                      filteredItems.map(item => (
                        <Combobox.Option
                          key={item.code}
                          value={item}
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-4 pr-4 ${active ? "bg-primary text-white" : "text-gray-900"}`
                          }
                        >
                          <div className="flex flex-col">
                            <span className="font-medium font-mono text-sm">
                              {formatCode(item.code)}
                            </span>
                            <span className="text-sm truncate opacity-90">
                              {item.title}
                            </span>
                          </div>
                        </Combobox.Option>
                      ))
                    )}
                  </Combobox.Options>
                </Combobox>
              </div>
            )}
          </div>
        )}

        <div
          className={`relative bg-white border rounded-md transition-colors duration-200 ${
            readOnly
              ? "border-gray-200 bg-gray-50 focus-within:border-gray-200 focus-within:ring-0 focus-within:outline-none cursor-default"
              : error
                ? "border-red-500 focus-within:ring-2 focus-within:ring-red-500 focus-within:border-red-500 cursor-text"
                : "border-gray-300 hover:border-gray-400 focus-within:ring-2 focus-within:ring-primary focus-within:border-primary cursor-text"
          } ${disabled ? "opacity-50 cursor-not-allowed bg-gray-50" : ""}`}
        >
          <input
            type="text"
            value={value || ""}
            onChange={e => onChange(e.target.value || null)}
            onBlur={readOnly ? undefined : onBlur}
            disabled={disabled}
            readOnly={readOnly}
            className={`w-full py-2 px-3 text-sm bg-transparent border-none focus:ring-0 focus:outline-none placeholder-gray-400 rounded-md ${readOnly ? "cursor-default" : ""} ${disabled ? "cursor-not-allowed" : ""}`}
            placeholder={
              readOnly ? "" : t("combinedNomenclatureInput.inputPlaceholder")
            }
          />
        </div>
      </div>
      <FormError error={error} params={errorParams} />
    </div>
  );
}
